import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import zhCN from "antd/es/date-picker/locale/zh_CN";
import message from "../../components/Message";
import "./index.css";

export default function ManagersModule() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [showChatButton, setShowChatButton] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [maxActCount, setMaxActCount] = useState(0);
  const [expireTime, setExpireTime] = useState(null);
  const permissionOptions = [
    "智能养号",
    "快捷回复",
    "自动翻译",
    "分流链接",
    "动态评论",
  ];
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8080";
  const token = useMemo(() => localStorage.getItem("token"), []);

  const loadList = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const resp = await fetch(`${baseUrl}/admin/manager/list`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.toast(msg || "加载失败");
        return;
      }
      setList(result?.data?.list || []);
    } catch (error) {
      message.toast("加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const openAdd = () => {
    setEditingId("");
    setAccount("");
    setPassword("");
    setShowChatButton(false);
    setPermissions(permissionOptions.map((name) => ({ name, expireTime: null })));
    setMaxActCount(0);
    setExpireTime(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setAccount(record.account || "");
    setPassword(record.password || "");
    setShowChatButton(Boolean(record.showChatButton));
    if (typeof record.permissions === "string" && record.permissions.trim()) {
      try {
        const parsed = JSON.parse(record.permissions);
        if (Array.isArray(parsed)) {
          const normalized = parsed
            .map((item) => {
              if (!item) return null;
              if (typeof item === "string") {
                const name = item.trim();
                if (!name) return null;
                return { name, expireTime: null };
              }
              if (typeof item === "object") {
                const name = String(item.name || "").trim();
                if (!name) return null;
                const expireTime = item.expireTime ?? null;
                return { name, expireTime };
              }
              return null;
            })
            .filter(Boolean);
          setPermissions(normalized);
        } else {
          setPermissions([]);
        }
      } catch {
        setPermissions([]);
      }
    } else {
      setPermissions([]);
    }
    setMaxActCount(Number(record.maxActCount ?? 0) || 0);
    setExpireTime(record.expireTime ? dayjs(record.expireTime) : null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!account.trim()) {
      message.toast("请输入账号");
      return;
    }
    if (!password.trim()) {
      message.toast("请输入密码");
      return;
    }
    setSaving(true);
    try {
      const params = new URLSearchParams({
        account,
        password,
      });
      params.set("showChatButton", String(showChatButton));
      params.set("permissions", JSON.stringify(permissions));
      params.set("maxActCount", String(Number(maxActCount) || 0));
      params.set("expireTime", expireTime ? String(expireTime.valueOf()) : "0");
      if (editingId) {
        params.set("id", editingId);
      }
      const endpoint = editingId ? "edit" : "add";
      const resp = await fetch(`${baseUrl}/admin/manager/${endpoint}?${params}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.toast(msg || "保存失败");
        return;
      }
      message.success("保存成功");
      setModalOpen(false);
      setEditingId("");
      setAccount("");
      setPassword("");
      await loadList();
    } catch (error) {
      message.toast("保存失败");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const params = new URLSearchParams({ id: record.id });
      const resp = await fetch(`${baseUrl}/admin/manager/delete?${params}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.toast(msg || "删除失败");
        return;
      }
      message.success("删除成功");
      setList((prev) => prev.filter((item) => item.id !== record.id));
    } catch (error) {
      message.toast("删除失败");
    }
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      loadList();
      return;
    }
    if (loading) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ keyword });
      const resp = await fetch(`${baseUrl}/admin/manager/search?${params}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.toast(msg || "搜索失败");
        return;
      }
      setList(result?.data?.list || []);
    } catch (error) {
      message.toast("搜索失败");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "账号",
      dataIndex: "account",
      key: "account",
      width: 220,
      align: "center",
      fixed: "left",
    },
    { title: "密码", dataIndex: "password", key: "password", width: 180, align: "center" },
    {
      title: "WhatsApp账号额度",
      dataIndex: "maxActCount",
      key: "maxActCount",
      width: 160,
      align: "center",
      render: (value) => (Number(value) > 0 ? Number(value) : "不限"),
    },
    {
      title: "到期时间",
      dataIndex: "expireTime",
      key: "expireTime",
      width: 200,
      align: "center",
      render: (value) => (value ? String(value) : "未设置"),
    },
    {
      title: "菜单权限",
      dataIndex: "permissions",
      key: "permissions",
      width: 260,
      align: "center",
      render: (value) => {
        if (typeof value !== "string" || !value.trim()) {
          return <Tag color="default">未开通</Tag>;
        }
        try {
          const parsed = JSON.parse(value);
          if (!Array.isArray(parsed) || parsed.length === 0) {
            return <Tag color="default">未开通</Tag>;
          }
          return (
            <Space size={[4, 4]} wrap>
              {parsed.map((item) => {
                const name =
                  typeof item === "string"
                    ? item
                    : item && typeof item === "object"
                    ? String(item.name || "")
                    : "";
                const expire = item && typeof item === "object" ? item.expireTime : null;
                const key = `${name}__${expire || ""}`;
                if (!name.trim()) return null;
                return (
                  <Tag key={key} color="blue">
                    {name}
                  </Tag>
                );
              })}
            </Space>
          );
        } catch {
          return <Tag color="default">未开通</Tag>;
        }
      },
    },
    {
      title: "查看聊天",
      dataIndex: "showChatButton",
      key: "showChatButton",
      width: 140,
      align: "center",
      render: (value) =>
        value ? <Tag color="green">显示</Tag> : <Tag color="red">隐藏</Tag>,
    },
    {
      title: "创建时间",
      dataIndex: "createTime",
      key: "createTime",
      width: 220,
      align: "center",
      render: (value) => (value ? String(value) : ""),
    },
    {
      title: "操作",
      key: "action",
      width: 160,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确认删除该工作室？" onConfirm={() => handleDelete(record)}>
            <Button type="link" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
        <Space>
          <Button type="primary" onClick={openAdd}>
            +添加工作室
          </Button>
          <Button onClick={loadList} loading={loading}>
            刷新
          </Button>
        </Space>
        <Input.Search
          placeholder="搜索账号或ID"
          allowClear
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          onSearch={handleSearch}
          enterButton={<Button type="default">搜索</Button>}
          prefix={<SearchOutlined />}
          style={{ maxWidth: 420, marginLeft: "auto" }}
        />
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        bordered
        pagination={{ pageSize: 10 }}
        tableLayout="fixed"
        scroll={{ x: 1400 }}
      />
      <Modal
        title={editingId ? "编辑工作室" : "添加工作室"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            取消
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSave}>
            保存
          </Button>,
        ]}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ marginBottom: 6, fontSize: 14, color: "#6b7280" }}>
              <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
              账号
            </div>
            <Input
              placeholder="请输入账号"
              autoComplete="off"
              value={account}
              onChange={(event) => setAccount(event.target.value)}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontSize: 14, color: "#6b7280" }}>
              <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
              密码
            </div>
            <Input.Password
              placeholder="请输入密码"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 14, color: "#6b7280" }}>
              <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
              员工列表显示“查看聊天”
            </div>
            <Switch checked={showChatButton} onChange={setShowChatButton} />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontSize: 14, color: "#6b7280" }}>
              <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
              到期时间
            </div>
            <DatePicker
              locale={zhCN}
              showTime
              style={{ width: "100%" }}
              value={expireTime}
              onChange={(value) => setExpireTime(value)}
              placeholder="选择到期时间"
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontSize: 14, color: "#6b7280" }}>
              <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
              WhatsApp账号额度
            </div>
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              value={maxActCount}
              onChange={(value) => setMaxActCount(value ?? 0)}
              placeholder="0表示不限"
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontSize: 14, color: "#6b7280" }}>
              <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
              菜单权限
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {permissionOptions.map((name) => {
                const item = permissions.find((p) => p && p.name === name) || null;
                const checked = Boolean(item);
                const rawExpire = item?.expireTime ? String(item.expireTime).trim() : "";
                const value = rawExpire ? dayjs(rawExpire.replace(" ", "T")) : null;
                return (
                  <div
                    key={name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={(event) => {
                        const nextChecked = Boolean(event?.target?.checked);
                        setPermissions((prev) => {
                          const arr = Array.isArray(prev) ? prev : [];
                          const exists = arr.find((p) => p && p.name === name);
                          if (nextChecked) {
                            if (exists) return arr;
                            return [...arr, { name, expireTime: null }];
                          }
                          return arr.filter((p) => !(p && p.name === name));
                        });
                      }}
                    >
                      {name}
                    </Checkbox>
                    <DatePicker
                      locale={zhCN}
                      showTime
                      allowClear
                      disabled={!checked}
                      value={value}
                      format="YYYY-MM-DD HH:mm:ss"
                      onChange={(next) => {
                        setPermissions((prev) => {
                          const arr = Array.isArray(prev) ? prev : [];
                          return arr.map((p) => {
                            if (!p || p.name !== name) return p;
                            return {
                              ...p,
                              expireTime: next ? next.format("YYYY-MM-DD HH:mm:ss") : null,
                            };
                          });
                        });
                      }}
                      placeholder="不限"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
